import type { components } from '@feedfm/clips-services-api';

type Collection = components['schemas']['Collection'];

type CollectionsResponseRow =
  components['schemas']['CollectionsResponse']['collections'][number];

/**
 * Enum defining the available sorting options for collections.
 *
 * For sorting clips within a collection:
 *   - NAME: Sorts clips alphabetically by title (A-Z)
 *   - CREATE_DATE: Sorts clips by creation date, newest first
 *
 * For sorting the list of collections:
 *   - DEFAULT: Uses backend order (pinned collections first, then by sort_index)
 *   - DATE_ADDED: Sorts collections by publish date, newest first
 *   - ALPHABETICAL: Sorts collections alphabetically by name
 */
export enum CollectionSortBy {
  // Clip sorting options
  NAME = 'name',
  CREATE_DATE = 'createDate',
  // Collection list sorting options
  DEFAULT = 'default',
  DATE_ADDED = 'date-added',
  ALPHABETICAL = 'alphabetical',
}

/**
 * Returns the display text for a collection list sort option.
 * Use this function to ensure consistent labeling across the UI.
 */
export function collectionSortByText(sortBy: CollectionSortBy): string {
  switch (sortBy) {
    case CollectionSortBy.DATE_ADDED:
      return 'Most Recent';
    case CollectionSortBy.ALPHABETICAL:
      return 'Alphabetical';
    case CollectionSortBy.DEFAULT:
      return 'Default';
    case CollectionSortBy.NAME:
      return 'Name';
    case CollectionSortBy.CREATE_DATE:
      return 'Date Added';
  }
}

/**
 * Sorts rows from `GET /collections` (summary + artwork) for the studio list.
 * Clip-level sort options leave order unchanged.
 */
export function sortCollectionsResponseRows(
  rows: CollectionsResponseRow[],
  sortBy: CollectionSortBy,
): CollectionsResponseRow[] {
  const copy = [...rows];

  switch (sortBy) {
    case CollectionSortBy.ALPHABETICAL:
      copy.sort((a, b) =>
        a.summary.collectionName.localeCompare(b.summary.collectionName),
      );
      return copy;
    case CollectionSortBy.DATE_ADDED:
      copy.sort((a, b) => {
        const aDate = a.summary.publishedOn
          ? new Date(a.summary.publishedOn).getTime()
          : 0;
        const bDate = b.summary.publishedOn
          ? new Date(b.summary.publishedOn).getTime()
          : 0;

        if (aDate === 0 && bDate === 0) {
          return b.summary.collectionId - a.summary.collectionId;
        }
        if (aDate === 0) return 1;
        if (bDate === 0) return -1;
        return bDate - aDate;
      });
      return copy;
    case CollectionSortBy.DEFAULT:
    case CollectionSortBy.NAME:
    case CollectionSortBy.CREATE_DATE:
      return copy;
  }
}

/**
 * Sorts a collection's clips based on the specified sort criteria.
 *
 * This utility function is used to ensure consistent sorting behavior across the application,
 * particularly for collection playback order and UI display. The sorting is applied to the
 * clips array within the collection, returning a new collection object with sorted clips.
 *
 * @param collection - The collection object containing clips to be sorted
 * @param sortBy - The sorting criteria to apply
 *   - CollectionSortBy.NAME: Sorts clips alphabetically by title (A-Z)
 *   - CollectionSortBy.CREATE_DATE: Sorts clips by creation date, newest first (descending)
 *
 * @returns A new collection object with the clips array sorted according to the specified criteria
 * ```
 */
export const sortCollection = (
  collection: Collection,
  sortBy: CollectionSortBy,
): Collection => {
  return {
    ...collection,
    clips: [...collection.clips].sort((a, b) => {
      if (sortBy === CollectionSortBy.NAME) {
        return a.title.localeCompare(b.title);
      } else {
        // sortBy === CollectionSortBy.CREATE_DATE - newest first (descending)
        return (
          new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
        );
      }
    }),
  } as Collection;
};
